import React, { useCallback, useRef } from 'react'

import { Layout } from 'antd'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { getBackgroundColor } from '../../../../components/hocs/withOverriddenTheme'
import { AUTHENTICATED_ROUTES } from '../../../../utils/routing/routes'

import AuthenticatedHeader from './components/AuthenticatedHeader'
import PageTitle from './components/PageTitle'

const { Content } = Layout

const SLayout = styled(/** @type {any} */ (Layout))`
  display: flex !important;
  flex-direction: column !important;
  min-height: calc(100vh - 64px) !important;
`

const SContent = styled(/** @type {any} */ (Content))`
  background: "${getBackgroundColor}";
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 40px !important;
`

const SFooter = styled.footer`
  align-items: center;
  background-color: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding: 16px 24px;
  width: 100%;
`

const FooterCopyright = styled.span`
  color: #4c4c4c;
  font-size: 13px;
  padding-left: 50px;
`

const FooterLinks = styled.nav`
  align-items: center;
  display: flex;
  gap: 24px;
`

const FooterLink = styled.a`
  color: #4c4c4c;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #33628e;
  }
`

const FooterRouterLink = styled(Link)`
  color: #4c4c4c;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #33628e;
  }
`

const SkipToContent = styled.a`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  white-space: nowrap;
  width: 1px;
  z-index: 9999;

  &:focus {
    background-color: #ffffff;
    clip: auto;
    clip-path: none;
    color: #000000;
    height: auto;
    left: 8px;
    outline: 2px solid #000000;
    overflow: visible;
    padding: 8px 16px;
    position: fixed;
    top: 8px;
    width: auto;
  }
`

/** @param {{ children: React.ReactNode, gutters?: boolean, title?: string }} props */
const AuthenticatedLayout = props => {
  const mainRef = useRef(null)

  const focusMain = useCallback(e => {
    e.preventDefault()
    if (mainRef.current instanceof HTMLElement) mainRef.current.focus()
  }, [])

  const onSkipKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') focusMain(e)
    },
    [focusMain],
  )

  return (
    <SLayout>
      <SkipToContent href="#main-start" onClick={focusMain} onKeyDown={onSkipKeyDown}>
        Skip to main content
      </SkipToContent>
      <AuthenticatedHeader />
      <SContent className={props.gutters ? 'gutters' : ''}>
        <main id="main-content">
          <div id="main-start" tabIndex={-1} ref={mainRef} />
          {props.title && <PageTitle title={props.title} />}
          {props.children}
        </main>
      </SContent>
      <SFooter>
        <FooterCopyright>© {new Date().getFullYear()} Steppingblocks</FooterCopyright>
        <FooterLinks aria-label="Footer navigation">
          <FooterRouterLink to={AUTHENTICATED_ROUTES.ALL_SECTIONS}>All Sections</FooterRouterLink>
          <FooterLink
            href="https://www.steppingblocks.com/about-us"
            target="_blank"
            rel="noopener noreferrer"
          >
            About
          </FooterLink>
          <FooterLink
            href="https://www.steppingblocks.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </FooterLink>
        </FooterLinks>
      </SFooter>
    </SLayout>
  )
}

export default AuthenticatedLayout
